import _ from 'lodash'
import qs from 'qs'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import keycloak from '@/keycloak'

import { RootState } from '@/redux/rootReducer'

import { getEnv } from '@/utils/helpers'
import { cacher } from '@/utils/rtkQueryCacheUtils'
import { ensureFreshKeycloakToken, refreshKeycloakToken } from '@/utils/auth-token'

export enum Tag {
  ACCOUNT_GROUP = 'AccountGroup',
  ACCOUNT = 'Account',
  ACTS = 'Acts',
  ADMIN = 'Admin',
  API_KEY = 'ApiKey',
  AVAILABLE_SHIFTS = 'AvailableShifts',
  CHAT_ROOM = 'ChatRoom',
  CONSULTATION = 'Consultation',
  CONSULTATION_SETTINGS = 'ConsultationSettings',
  CONTRACT = 'Contract',
  ESTABLISHMENT = 'Establishment',
  EXPERTISE_MEETING = 'ExpertiseMeeting',
  EXPERTISE = 'Expertise',
  LICENSE = 'License',
  MEETING_LINK = 'MeetingLink',
  MEETING_LINK_ORDER = 'MeetingLinkOrder',
  MY_SHIFTS = 'MyShifts',
  NETWORK = 'Network',
  NETWORK_INVITATION = 'NetworkInvitation',
  NETWORK_MEMBER = 'NetworkMember',
  ORDER = 'Order',
  PLANNING = 'Planning',
  PATIENT_QUEUE = 'PatientQueue',
  POLICY = 'Policy',
  SHIFT_EXCEPTION = 'ShiftException',
  SHIFT_PROPOSAL = 'ShiftProposal',
  SHIFT_TEMPLATE = 'ShiftTemplate',
  SHIFT = 'Shift',
  RECORDING = 'Recording',
  REPORT = 'Report',
  REPORT_TEMPLATE = 'ReportTemplate',
  STAFF = 'Staff',
  TRANSCRIPTION = 'Transcription',
  WAITING_ROOM = 'WaitingRoom',
  WHATSAPP_BINDING = 'WhatsappBinding',
  FORM_TEMPLATE = 'FORM_TEMPLATE',
  FORM_SUBMISSION = 'FORM_SUBMISSION',
  SHIFTS = 'Shifts',
  AGENT_CHAT_SESSIONS = 'AgentChatSessions',
  AGENT_CHAT_HISTORY = 'AgentChatHistory',
  KNOWLEDGE_BASE_DOCUMENT = 'KnowledgeBaseDocument',
  KNOWLEDGE_BASE_COLLECTION = 'KnowledgeBaseCollection',
  FILES = 'Files',
  PATIENT = 'Patient',
  SPECIALITY_OPTION = 'SpecialityOption',
  CONSULTATION_REASON = 'ConsultationReason',
  MEDICAL_ACT_TEMPLATE = 'MedicalActTemplate',
  MEDICAL_ACT_RECORD = 'MedicalActRecord',
  PREGNANCY = 'Pregnancy',
  TIME_TRACKING_CONSENT = 'TimeTrackingConsent',
  TIME_TRACKING_SESSION = 'TimeTrackingSession',
  TIME_TRACKING_NOTIFICATION = 'TimeTrackingNotification',
}

const tags = Object.keys(Tag).map((key) => Tag[key as keyof typeof Tag])

export const baseQuery = fetchBaseQuery({
  baseUrl: getEnv('VITE_API_URL')! || 'http://localhost:3005',
  paramsSerializer: (params) => qs.stringify(params, { allowEmptyArrays: true, arrayFormat: 'brackets' }),
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = keycloak.token || (getState() as RootState).auth.tokens.access
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    if (getEnv('NODE_ENV') !== 'production') headers.set('ngrok-skip-browser-warning', '69420')
    return headers
  },
})

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  await ensureFreshKeycloakToken()

  let result = await baseQuery(args, api, extraOptions)
  if (result.error && result.error.status === 401) {
    try {
      const token = await refreshKeycloakToken()
      if (token) {
        result = await baseQuery(args, api, extraOptions)
      }
    } catch {
      // If the refresh token is expired or rejected, fall through to logout.
    }

    if (result.error && result.error.status === 401) {
      keycloak.logout()
    }
  }
  if (result.data && typeof result.data === 'object' && 'datas' in result.data) {
    result.data = _.get(result.data, 'datas')
  }
  return result
}

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [...cacher.defaultTags, ...tags],
  endpoints: () => ({}),
})
